import React, { useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { ENDPOINT } from 'constants';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';
import DataTable from 'examples/Tables/DataTable';

const REMINDER_TYPE_COLORS = {
  account: 'info',
  system: 'dark',
  marketing: 'warning',
  support: 'success',
  engagement: 'primary',
  payment: 'error',
  subscription: 'secondary',
  order: 'info',
  payment_failed: 'error',
  invoice: 'warning',
  renewal: 'success',
  legal: 'dark',
  refund: 'error',
  overdue: 'error',
  suspension: 'error',
  cart: 'warning',
  due: 'warning',
  trial: 'secondary',
  design: 'primary',
  payment_cancelled: 'error',
  payment_recovered: 'success',
  payment_overdue: 'error',
};

const SERVICE_COLORS = {
  'subscription-renewal-job': 'success',
  'abandoned-cart-job': 'warning',
  'due-date-job': 'info',
  'overdue-job': 'error',
  'payment-job': 'primary',
  'suspension-job': 'error',
  'welcome-job': 'secondary',
};

const PALETTE = ['info', 'success', 'warning', 'error', 'primary', 'secondary'];

// Deterministic fallback so any job name not in SERVICE_COLORS still gets a
// stable, distinct color instead of falling back to a single dull default.
const colorForService = (name) => {
  if (!name) return 'secondary';
  if (SERVICE_COLORS[name]) return SERVICE_COLORS[name];
  const hash = [...name].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length];
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Every card below is computed directly from real data — template composition from
// the `templates` list already fetched by the parent, and send activity from the
// email_histories table. Nothing here is placeholder/sample data.
const OverView = ({ templates = [] }) => {
  const { user } = useSelector((state) => state.auth);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${ENDPOINT}api/v1/email-history/all`, {
          headers: { Authorization: `Bearer ${user?.accessToken}` },
        });
        if (!cancelled) setEmailHistory(data.data || []);
      } catch (err) {
        console.error('Failed to fetch email history:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.accessToken]);

  const templateStats = useMemo(() => {
    const total = templates.length;
    const active = templates.filter((t) => t.isActive).length;
    const emailEnabled = templates.filter((t) => t.subject || t.body).length;
    const smsEnabled = templates.filter((t) => t.smsBody).length;

    const byCategory = {};
    templates.forEach((t) => {
      byCategory[t.reminderType] = (byCategory[t.reminderType] || 0) + 1;
    });
    const categoryBreakdown = Object.entries(byCategory)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);

    return { total, active, emailEnabled, smsEnabled, categoryBreakdown };
  }, [templates]);

  const sendStats = useMemo(() => {
    const now = Date.now();
    const last7 = emailHistory.filter((h) => now - new Date(h.createdAt).getTime() <= 7 * DAY_MS);
    const last30 = emailHistory.filter((h) => now - new Date(h.createdAt).getTime() <= 30 * DAY_MS);
    const services = new Set(emailHistory.map((h) => h.sentBy).filter(Boolean));

    return {
      total: emailHistory.length,
      last7: last7.length,
      last30: last30.length,
      serviceCount: services.size,
    };
  }, [emailHistory]);

  const recentActivity = useMemo(
    () => [...emailHistory].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [emailHistory],
  );

  const recentActivityColumns = [
    { Header: 'Sent to', accessor: 'sentTo', width: '17%' },
    { Header: 'Subject', accessor: 'subject', width: '29%' },
    { Header: 'Service', accessor: 'service', width: '14%' },
    { Header: 'Sent', accessor: 'sent', width: '22%' },
    { Header: 'Status', accessor: 'status', width: '10%' },
    { Header: 'Preview', accessor: 'preview', width: '8%', align: 'center' },
  ];

  const recentActivityRows = useMemo(
    () =>
      recentActivity.map((row) => ({
        sentTo: (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: '0.75rem',
                bgcolor: (theme) => theme.palette[colorForService(row.sentTo)]?.main || theme.palette.secondary.main,
              }}
            >
              {row.sentTo?.[0]?.toUpperCase() || '?'}
            </Avatar>
            <MDTypography variant="caption" fontWeight="medium">{row.sentTo}</MDTypography>
          </MDBox>
        ),
        subject: <MDTypography variant="button" fontWeight="regular">{row.subject}</MDTypography>,
        service: (
          <Chip
            label={row.sentBy || '—'}
            size="small"
            color={colorForService(row.sentBy)}
            variant="outlined"
          />
        ),
        sent: (
          <MDTypography variant="button" fontWeight="regular" fontFamily="monospace">
            {new Date(row.createdAt).toLocaleString()}
          </MDTypography>
        ),
        status: (
          <Chip
            icon={<Icon fontSize="small">{row.status === 'sent' ? 'check_circle' : 'error'}</Icon>}
            label={row.status}
            size="small"
            color={row.status === 'sent' ? 'success' : 'error'}
          />
        ),
        preview: (
          <Tooltip title="View email as received">
            <IconButton size="small" color="info" onClick={() => setPreviewEmail(row)}>
              <Icon>visibility</Icon>
            </IconButton>
          </Tooltip>
        ),
      })),
    [recentActivity],
  );

  const maxCategoryCount = Math.max(1, ...templateStats.categoryBreakdown.map((c) => c.count));

  const TOP_STATS = [
    {
      color: 'info',
      icon: 'description',
      title: 'Total templates',
      count: String(templateStats.total),
      percentage: { color: 'success', amount: String(templateStats.active), label: 'active' },
    },
    {
      color: 'success',
      icon: 'mail',
      title: 'Email-enabled',
      count: String(templateStats.emailEnabled),
      percentage: { color: 'secondary', amount: '', label: `of ${templateStats.total} total` },
    },
    {
      color: 'dark',
      icon: 'sms',
      title: 'SMS-enabled',
      count: String(templateStats.smsEnabled),
      percentage: { color: 'secondary', amount: '', label: `of ${templateStats.total} total` },
    },
    {
      color: 'secondary',
      icon: 'category',
      title: 'Categories in use',
      count: String(templateStats.categoryBreakdown.length),
      percentage: { color: 'secondary', amount: '', label: 'reminder-type groups' },
    },
  ];

  const SEND_STATS = [
    {
      color: 'primary',
      icon: 'send',
      title: 'Emails sent (all time)',
      count: String(sendStats.total),
      percentage: { color: 'secondary', amount: '', label: 'from email_histories' },
    },
    {
      color: 'info',
      icon: 'today',
      title: 'Sent — last 7 days',
      count: String(sendStats.last7),
      percentage: { color: 'secondary', amount: '', label: '' },
    },
    {
      color: 'info',
      icon: 'calendar_month',
      title: 'Sent — last 30 days',
      count: String(sendStats.last30),
      percentage: { color: 'secondary', amount: '', label: '' },
    },
    {
      color: 'dark',
      icon: 'apps',
      title: 'Sending services',
      count: String(sendStats.serviceCount),
      percentage: { color: 'secondary', amount: '', label: 'distinct sentBy sources' },
    },
  ];

  return (
    <MDBox>
      <MDBox mb={3}>
        <MDTypography variant="h5" fontWeight="bold">Overview</MDTypography>
        <MDTypography variant="button" color="text" fontWeight="regular">
          Real template composition and send activity — no sample data
        </MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        {TOP_STATS.map((s) => (
          <Grid item xs={12} md={6} lg={3} key={s.title}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color={s.color}
                icon={s.icon}
                title={s.title}
                count={s.count}
                percentage={s.percentage}
              />
            </MDBox>
          </Grid>
        ))}
      </Grid>

      {loading ? (
        <MDBox display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </MDBox>
      ) : (
        <Grid container spacing={3} mt={0.5}>
          {SEND_STATS.map((s) => (
            <Grid item xs={12} md={6} lg={3} key={s.title}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color={s.color}
                  icon={s.icon}
                  title={s.title}
                  count={s.count}
                  percentage={s.percentage}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>
      )}

      {error && (
        <MDTypography variant="caption" color="error" display="block" mt={1}>
          Couldn&apos;t load send activity — showing template stats only.
        </MDTypography>
      )}

      {/* Templates by category — real breakdown of the templates list by reminderType */}
      <Card sx={{ mt: 3 }}>
        <MDBox p={3} pb={1}>
          <MDTypography variant="h6">Templates by category</MDTypography>
          <MDTypography variant="caption" color="text">
            How the {templateStats.total} templates split across reminder-type groups
          </MDTypography>
        </MDBox>
        <MDBox px={3} pb={3}>
          {templateStats.categoryBreakdown.map((c) => (
            <MDBox key={c.label} display="flex" alignItems="center" gap={2} py={1}>
              <MDBox width="140px" flexShrink={0}>
                <MDTypography variant="button" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                  {c.label.replace(/_/g, ' ')}
                </MDTypography>
              </MDBox>
              <MDBox flex={1} height="20px" bgColor="grey-100" borderRadius="md" overflow="hidden">
                <MDBox
                  height="100%"
                  borderRadius="md"
                  bgColor={REMINDER_TYPE_COLORS[c.label] || 'secondary'}
                  sx={{ width: `${((c.count / maxCategoryCount) * 100).toFixed(0)}%` }}
                />
              </MDBox>
              <MDBox width="50px" flexShrink={0} textAlign="right">
                <MDTypography variant="button" fontWeight="medium">{c.count}</MDTypography>
              </MDBox>
            </MDBox>
          ))}
        </MDBox>
      </Card>

      {/* Recent activity — real rows from email_histories, most recent first */}
      <Card sx={{ mt: 3 }}>
        <MDBox p={3} pb={1}>
          <MDTypography variant="h6">Recent activity</MDTypography>
          <MDTypography variant="caption" color="text">
            All {recentActivity.length} emails actually sent, across every service
          </MDTypography>
        </MDBox>
        <MDBox px={2} pb={2}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </MDBox>
          ) : recentActivity.length === 0 ? (
            <MDBox py={4} textAlign="center">
              <MDTypography variant="caption" color="text">
                No emails have been sent yet.
              </MDTypography>
            </MDBox>
          ) : (
            <DataTable
              table={{ columns: recentActivityColumns, rows: recentActivityRows }}
              isSorted={false}
              entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
              showTotalEntries
              canSearch
              noEndBorder
            />
          )}
        </MDBox>
      </Card>

      {/* Email preview — the exact HTML content the recipient actually received */}
      <Dialog
        open={Boolean(previewEmail)}
        onClose={() => setPreviewEmail(null)}
        fullWidth
        maxWidth="md"
      >
        {previewEmail && (
          <>
            <DialogTitle>
              <MDTypography variant="h6">{previewEmail.subject}</MDTypography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              <MDBox p={3} pt={2}>
                <MDBox display="flex" flexWrap="wrap" gap={3} mb={1}>
                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block">Sent to</MDTypography>
                    <MDTypography variant="button" fontWeight="medium">{previewEmail.sentTo}</MDTypography>
                  </MDBox>
                  {previewEmail.sentFrom && (
                    <MDBox>
                      <MDTypography variant="caption" color="text" display="block">Sent from</MDTypography>
                      <MDTypography variant="button" fontWeight="medium">{previewEmail.sentFrom}</MDTypography>
                    </MDBox>
                  )}
                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block">Service</MDTypography>
                    <Chip
                      label={previewEmail.sentBy || '—'}
                      size="small"
                      color={colorForService(previewEmail.sentBy)}
                      variant="outlined"
                    />
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block">Sent</MDTypography>
                    <MDTypography variant="button" fontWeight="medium" fontFamily="monospace">
                      {new Date(previewEmail.createdAt).toLocaleString()}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="caption" color="text" display="block">Status</MDTypography>
                    <Chip
                      icon={<Icon fontSize="small">{previewEmail.status === 'sent' ? 'check_circle' : 'error'}</Icon>}
                      label={previewEmail.status}
                      size="small"
                      color={previewEmail.status === 'sent' ? 'success' : 'error'}
                    />
                  </MDBox>
                </MDBox>
              </MDBox>
              <Divider />
              <MDBox
                component="iframe"
                title="Email content preview"
                srcDoc={previewEmail.content}
                sandbox=""
                width="100%"
                height="520px"
                sx={{ border: 'none', display: 'block', bgcolor: 'white' }}
              />
            </DialogContent>
            <DialogActions>
              <MDButton color="secondary" onClick={() => setPreviewEmail(null)}>
                Close
              </MDButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </MDBox>
  );
};

export default OverView;
